import { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS } from "./constants";
import { RoleLegend } from "./RoleLegend";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, RotateCcw, Tag } from "lucide-react";

interface ForceGraphProps {
  seeds: VisualSeed[];
  nodes: VisualNode[];
  filteredNodeIds?: Set<string> | null;
  onNodeClick?: (nodeId: string) => void;
}

interface SimNode extends d3.SimulationNodeDatum {
  id: string;
  title: string;
  role: string;
  seedId: string;
  seedName: string;
  seedColor: string;
  entity: string;
  stage: string;
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  source: string;
  target: string;
}

/**
 * ForceGraph v2 — convex hull per seed + color per seed + smarter clustering.
 *
 * Improvements vs v1:
 *  - Convex hull (semi-transparent polygon) wokół każdej grupy seedów z color per seed
 *  - Większe nody (8px), grubszy stroke
 *  - Lepsza separacja klastrów (silniejsza siła odpychania między grupami)
 *  - Seed name jako duża etykieta nad hull (z dark background card)
 *  - Hover na node → cała grupa rozjaśnia się (hull saturated, inne hulls dim)
 *  - Color per seed matching Sunburst v2 (Tableau10)
 */
export function ForceGraph({ seeds, nodes, filteredNodeIds, onNodeClick }: ForceGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLabels, setShowLabels] = useState(nodes.length <= 80);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [hoveredSeedId, setHoveredSeedId] = useState<string | null>(null);

  const seedColorScale = useMemo(() => {
    return d3
      .scaleOrdinal<string, string>()
      .domain(seeds.map((s) => s.id))
      .range(d3.schemeTableau10);
  }, [seeds]);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const width = 1200;
    const height = 800;
    const seedNameMap = new Map(seeds.map((s) => [s.id, s.name]));

    const filteredNodes = filteredNodeIds
      ? nodes.filter((n) => filteredNodeIds.has(n.id))
      : nodes;

    const simNodes: SimNode[] = filteredNodes.map((n) => ({
      id: n.id,
      title: n.title,
      role: n.document_role,
      seedId: n.seed_id,
      seedName: seedNameMap.get(n.seed_id) || "?",
      seedColor: seedColorScale(n.seed_id),
      entity: n.primary_entity_raw,
      stage: n.engine_stage || "pending",
    }));

    // Group nodes by seed
    const seedGroups = new Map<string, SimNode[]>();
    for (const n of simNodes) {
      if (!seedGroups.has(n.seedId)) seedGroups.set(n.seedId, []);
      seedGroups.get(n.seedId)!.push(n);
    }

    // Star topology: hub → others (subtle skeleton lines)
    const simLinks: SimLink[] = [];
    for (const [, groupNodes] of seedGroups) {
      if (groupNodes.length < 2) continue;
      const hub = groupNodes[0].id;
      for (let i = 1; i < groupNodes.length; i++) {
        simLinks.push({ source: hub, target: groupNodes[i].id });
      }
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${width} ${height}`).style("width", "100%");

    const g = svg.append("g");

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.15, 6])
      .on("zoom", (event) => g.attr("transform", event.transform));
    svg.call(zoom);
    zoomRef.current = zoom;

    // Pre-position seeds on a circle for stable layout
    const seedArray = [...seedGroups.keys()];
    const angleStep = (2 * Math.PI) / Math.max(seedArray.length, 1);
    const orbitRadius = Math.min(width, height) / 2.5;
    const seedCentroids = new Map<string, { x: number; y: number }>();
    seedArray.forEach((seedId, i) => {
      seedCentroids.set(seedId, {
        x: width / 2 + Math.cos(i * angleStep - Math.PI / 2) * orbitRadius,
        y: height / 2 + Math.sin(i * angleStep - Math.PI / 2) * orbitRadius,
      });
    });

    // Initialize positions near centroids (faster convergence)
    simNodes.forEach((n) => {
      const c = seedCentroids.get(n.seedId);
      if (c) {
        n.x = c.x + (Math.random() - 0.5) * 30;
        n.y = c.y + (Math.random() - 0.5) * 30;
      }
    });

    const simulation = d3
      .forceSimulation(simNodes)
      .force(
        "link",
        d3.forceLink<SimNode, SimLink>(simLinks).id((d) => d.id).distance(35).strength(0.4),
      )
      .force("charge", d3.forceManyBody().strength(-120))
      .force("collision", d3.forceCollide().radius(14))
      .force(
        "cluster",
        d3.forceX<SimNode>((d) => seedCentroids.get(d.seedId)?.x || width / 2).strength(0.3),
      )
      .force(
        "clusterY",
        d3.forceY<SimNode>((d) => seedCentroids.get(d.seedId)?.y || height / 2).strength(0.3),
      );

    // === Hull layer (under everything) ===
    const hullGroup = g.append("g").attr("class", "hulls");

    // === Links layer (subtle scaffold) ===
    const link = g
      .append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(simLinks)
      .join("line")
      .attr("stroke", (d) => {
        const src = simNodes.find((n) => n.id === (d.source as unknown as string));
        return src?.seedColor || "#4b5563";
      })
      .attr("stroke-opacity", 0.2)
      .attr("stroke-width", 1.5);

    // === Nodes layer ===
    const node = g
      .append("g")
      .attr("class", "nodes")
      .selectAll("circle")
      .data(simNodes)
      .join("circle")
      .attr("r", 7)
      .attr("fill", (d) => ROLE_COLORS[d.role] || "#6b7280")
      .attr("stroke", (d) => d.seedColor)
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) {
        d3.select(this).attr("r", 11).attr("stroke-width", 3);
        setHoveredSeedId(d.seedId);
        const rect = svgRef.current?.getBoundingClientRect();
        const cx = event.clientX - (rect?.left || 0);
        const cy = event.clientY - (rect?.top || 0);
        setTooltip({
          x: cx,
          y: cy,
          lines: [d.title, `Seed: ${d.seedName}`, `Rola: ${d.role}`, `Encja: ${d.entity}`, `Etap: ${d.stage}`],
        });
      })
      .on("mousemove", function (event) {
        const rect = svgRef.current?.getBoundingClientRect();
        const cx = event.clientX - (rect?.left || 0);
        const cy = event.clientY - (rect?.top || 0);
        setTooltip((prev) => (prev ? { ...prev, x: cx, y: cy } : prev));
      })
      .on("mouseout", function () {
        d3.select(this).attr("r", 7).attr("stroke-width", 2);
        setHoveredSeedId(null);
        setTooltip(null);
      })
      .on("click", (_, d) => onNodeClick?.(d.id))
      .call(
        d3
          .drag<SVGCircleElement, SimNode>()
          .on("start", (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    // === Node labels ===
    const label = g
      .append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(simNodes)
      .join("text")
      .attr("font-size", "9px")
      .attr("fill", "#d1d5db")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .attr("dy", 12)
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(0,0,0,0.7)")
      .attr("stroke-width", 2.5)
      .attr("pointer-events", "none")
      .attr("display", showLabels ? "block" : "none")
      .text((d) => (d.title.length > 28 ? d.title.slice(0, 25) + "…" : d.title));

    // === Seed labels (large, anchored to hull) ===
    const seedLabelGroup = g.append("g").attr("class", "seed-labels").attr("pointer-events", "none");

    // === Tick handler ===
    simulation.on("tick", () => {
      // Update links
      link
        .attr("x1", (d) => (d.source as unknown as { x: number }).x)
        .attr("y1", (d) => (d.source as unknown as { y: number }).y)
        .attr("x2", (d) => (d.target as unknown as { x: number }).x)
        .attr("y2", (d) => (d.target as unknown as { y: number }).y);

      // Update nodes
      node.attr("cx", (d) => d.x!).attr("cy", (d) => d.y!);
      label.attr("x", (d) => d.x!).attr("y", (d) => d.y! + 10);

      // Compute hulls
      const hullsData: Array<{ seedId: string; seedName: string; color: string; points: [number, number][]; centroid: [number, number] }> = [];
      for (const [seedId, groupNodes] of seedGroups) {
        const points = groupNodes
          .filter((n) => n.x !== undefined && n.y !== undefined)
          .map((n) => [n.x!, n.y!] as [number, number]);
        if (points.length < 2) continue;
        // Pad each point with surrounding offsets for thicker hull
        const padded: [number, number][] = [];
        const PAD = 22;
        for (const [px, py] of points) {
          padded.push([px - PAD, py - PAD], [px + PAD, py - PAD], [px - PAD, py + PAD], [px + PAD, py + PAD]);
        }
        const hull = d3.polygonHull(padded);
        if (!hull || hull.length < 3) continue;
        const cx = d3.mean(points, (p) => p[0]) || 0;
        const cy = d3.mean(points, (p) => p[1]) || 0;
        hullsData.push({
          seedId,
          seedName: seedNameMap.get(seedId) || "?",
          color: seedColorScale(seedId),
          points: hull,
          centroid: [cx, cy],
        });
      }

      // Render hulls
      const hulls = hullGroup.selectAll<SVGPathElement, typeof hullsData[number]>("path")
        .data(hullsData, (d) => d.seedId);
      hulls.exit().remove();
      const hullsEnter = hulls.enter().append("path");
      hullsEnter
        .merge(hulls)
        .attr("d", (d) => `M${d.points.map((p) => p.join(",")).join("L")}Z`)
        .attr("fill", (d) => d.color)
        .attr("fill-opacity", 0.12)
        .attr("stroke", (d) => d.color)
        .attr("stroke-opacity", 0.5)
        .attr("stroke-width", 2)
        .attr("stroke-linejoin", "round");

      // Render seed labels (find topmost point of hull for label position)
      const labels = seedLabelGroup
        .selectAll<SVGGElement, typeof hullsData[number]>("g.seed-label")
        .data(hullsData, (d) => d.seedId);
      labels.exit().remove();
      const labelsEnter = labels.enter().append("g").attr("class", "seed-label");
      labelsEnter.append("rect");
      labelsEnter.append("text");

      const merged = labelsEnter.merge(labels);
      merged.each(function (d) {
        const topY = d3.min(d.points, (p) => p[1]) || d.centroid[1];
        const sel = d3.select(this);
        sel.attr("transform", `translate(${d.centroid[0]}, ${topY - 18})`);
        const text = d.seedName.length > 30 ? d.seedName.slice(0, 27) + "…" : d.seedName;
        const charWidth = 6.5;
        const padX = 10;
        const textWidth = text.length * charWidth + padX * 2;
        sel
          .select("rect")
          .attr("x", -textWidth / 2)
          .attr("y", -12)
          .attr("width", textWidth)
          .attr("height", 22)
          .attr("rx", 4)
          .attr("fill", d.color)
          .attr("fill-opacity", 0.95)
          .attr("stroke", "rgba(0,0,0,0.5)")
          .attr("stroke-width", 1);
        sel
          .select("text")
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("y", 0)
          .attr("font-size", "11px")
          .attr("font-weight", "600")
          .attr("fill", "#ffffff")
          .text(text);
      });
    });

    return () => {
      simulation.stop();
    };
  }, [seeds, nodes, filteredNodeIds, onNodeClick, showLabels, seedColorScale]);

  // Apply hover dim effect to non-hovered hulls/links/nodes
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    if (!hoveredSeedId) {
      svg.selectAll(".hulls path").attr("fill-opacity", 0.12).attr("stroke-opacity", 0.5);
      svg.selectAll(".links line").attr("stroke-opacity", 0.2);
      svg.selectAll(".nodes circle").attr("fill-opacity", 1);
      return;
    }
    svg.selectAll<SVGPathElement, { seedId: string }>(".hulls path").attr("fill-opacity", function () {
      const d = d3.select(this).datum() as { seedId: string };
      return d.seedId === hoveredSeedId ? 0.28 : 0.05;
    });
    svg.selectAll<SVGCircleElement, SimNode>(".nodes circle").attr("fill-opacity", function () {
      const d = d3.select(this).datum() as SimNode;
      return d.seedId === hoveredSeedId ? 1 : 0.3;
    });
  }, [hoveredSeedId]);

  const handleZoom = (factor: number) => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.scaleBy, factor);
  };

  const handleReset = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(500).call(zoomRef.current.transform, d3.zoomIdentity);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <RoleLegend />
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLabels(!showLabels)}
            className="h-7 text-xs"
          >
            <Tag className="h-3 w-3 mr-1" /> {showLabels ? "Ukryj" : "Pokaż"} etykiety
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleZoom(1.3)}>
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => handleZoom(0.7)}>
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleReset}>
            <RotateCcw className="h-3 w-3 mr-1" /> Reset
          </Button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="relative border rounded-lg bg-background/50 overflow-hidden"
        style={{ minHeight: 700 }}
      >
        <svg ref={svgRef} className="w-full" style={{ minHeight: 700 }} />
        {tooltip && (
          <div
            className="absolute bg-popover border rounded-lg px-3 py-2 text-xs shadow-xl pointer-events-none z-50 max-w-[300px] animate-in fade-in"
            style={{
              left: Math.min(tooltip.x + 12, (containerRef.current?.clientWidth || 1000) - 310),
              top: Math.max(tooltip.y - 10, 8),
            }}
          >
            {tooltip.lines.map((line, i) => (
              <div
                key={i}
                className={i === 0 ? "font-semibold mb-1" : "text-muted-foreground text-[11px]"}
              >
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
